<script lang="ts">
import { defineComponent } from 'vue';
import { liteClient } from 'algoliasearch/lite';
import SearchItemCard from './cards/SearchItemCard.vue';

import {
  AisHits,
  AisInstantSearch,
  AisSearchBox,
  AisStateResults,
  AisStats,
} from 'vue-instantsearch/vue3/es';

type SearchHit = {
  objectID: string;
  title: string;
  description: string | null;
  url: string;
  sectionHandle: string;
  breadcrumbs: string;
};

export default defineComponent({
  components: {
    AisHits,
    AisInstantSearch,
    AisSearchBox,
    AisStateResults,
    AisStats,
    SearchItemCard,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    environment: {
      type: String,
      required: true,
    },
    isDesktop: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      searchClient: liteClient(this.appId, this.searchApiKey),
      isOpen: false,
    };
  },
  computed: {
    indexName() {
      return `${this.environment}_headerSearch`;
    },
  },
  watch: {
    isOpen(newVal: boolean) {
      if (newVal) {
        document.body.classList.add('overflow-hidden');
        this.$nextTick(() => {
          this.focusSearchInput();
        });
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    },
  },
  methods: {
    focusSearchInput() {
      const { searchBox } = this.$refs as {
        searchBox: { $refs: { searchInput: { $refs: { input: HTMLInputElement } } } };
      };
      const { searchInput } = searchBox.$refs;
      const { input } = searchInput.$refs;
      // Focus the search input (using autofocus is only reliable on page load)
      input.focus();
    },
    groupByProp(array: SearchHit[], key: keyof SearchHit) {
      return array.reduce(
        (groups, item) => {
          const group = item[key] || 'Other';
          groups[group] = groups[group] || [];
          groups[group].push(item);
          return groups;
        },
        {} as Record<string, SearchHit[]>,
      );
    },
    openSearch() {
      this.isOpen = true;
    },
    closeSearch() {
      this.isOpen = false;
    },
  },
});
</script>
<template>
  <div class="header-search text-ick-black-900" :class="{ 'px-6 py-4': !isDesktop }">
    <!-- Search Trigger -->
    <button
      v-if="isDesktop"
      type="button"
      class="hidden cursor-pointer text-ick-black-900 transition-colors duration-300 hover:text-ick-lime-500 md:block"
      aria-label="Search"
      @click="openSearch"
    >
      <svg width="26" height="24" viewBox="0 0 26 24" fill="none" aria-hidden="true">
        <g stroke="currentColor" stroke-width="2">
          <circle
            cx="11.2917"
            cy="11.2917"
            r="10.2867"
            transform="rotate(-0.0250705 11.2917 11.2917)"
          />
          <line x1="18.9854" y1="17.7408" x2="24.3601" y2="23.1154" />
        </g>
      </svg>
    </button>
    <button
      v-else
      type="button"
      class="relative mx-auto flex w-full max-w-77.75 cursor-pointer items-center rounded-3xl border-2 border-ick-neutral-400 bg-white py-2.5 pr-12 pl-6 text-left text-sm font-medium transition-colors lg:w-50"
      aria-label="Search"
      @click="openSearch"
    >
      <span>Search for your subject</span>
      <svg
        class="absolute top-1/2 right-4 -translate-y-1/2"
        width="20"
        height="20"
        viewBox="0 0 26 26"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M25 25L18.0711 18.0711M18.0711 18.0711C19.8807 16.2614 21 13.7614 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21C13.7614 21 16.2614 19.8807 18.0711 18.0711Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!-- Search Overlay -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-show="isOpen"
          class="fixed inset-0 z-9999 touch-auto bg-black/50"
          @click.self="closeSearch"
        >
          <ais-instant-search
            :search-client="searchClient"
            :index-name="indexName"
            class="fixed top-24 left-1/2 flex max-h-[80dvh] w-[90vw] max-w-container -translate-x-1/2 flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
          >
            <!-- Search Header -->
            <div class="shrink-0 bg-ick-neutral-200 pt-3 pb-10">
              <div class="px-3 text-right">
                <button
                  type="button"
                  class="cursor-pointer text-white transition-colors hover:text-ick-lime-500"
                  aria-label="Close search"
                  @click="closeSearch"
                >
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                    <ellipse cx="14" cy="14" rx="14" ry="14" fill="currentColor" />
                    <g stroke="#1E201E" stroke-width="2" stroke-linecap="round">
                      <path d="M18 10L10 18" />
                      <path d="M18 18L10 10" />
                    </g>
                  </svg>
                </button>
              </div>
              <ais-search-box
                ref="searchBox"
                show-loading-indicator
                placeholder="Search for your subject"
                :class-names="{
                  'ais-SearchBox': 'max-w-150 mx-auto px-8',
                  'ais-SearchBox-form': 'relative',
                  'ais-SearchBox-input':
                    'w-full rounded-3xl border-2 border-ick-neutral-400 bg-white py-3 text-sm font-medium pr-12 pl-6 [&::-webkit-search-cancel-button]:hidden',
                  'ais-SearchBox-reset': 'hidden',
                  'ais-SearchBox-submit': 'absolute right-4 top-1/2 -translate-y-1/2',
                  'ais-SearchBox-loadingIndicator': 'absolute right-4 top-1/2 -translate-y-1/2',
                }"
              >
                <template #submit-icon>
                  <svg width="26" height="24" viewBox="0 0 26 24" fill="none">
                    <g stroke="currentColor" stroke-width="2">
                      <circle
                        cx="11.2917"
                        cy="11.2917"
                        r="10.2867"
                        transform="rotate(-0.0250705 11.2917 11.2917)"
                      />
                      <line
                        x1="18.9854"
                        y1="17.7408"
                        x2="24.3601"
                        y2="23.1154"
                        stroke="currentColor"
                        stroke-width="2"
                      />
                    </g>
                  </svg>
                </template>
              </ais-search-box>
            </div>

            <!-- Search Results -->
            <div class="min-h-0 flex-1 overflow-y-auto px-8 py-3">
              <ais-stats
                :class-names="{
                  'ais-Stats-text': 'hidden',
                }"
              >
                <template #default="{ query }">
                  <div v-if="query">
                    <ais-state-results>
                      <template #default="{ results: { hits } }">
                        <ais-hits v-if="hits.length > 0">
                          <template #default="{ items }">
                            <div class="space-y-4">
                              <div
                                v-for="(groupItems, sectionHandle) in groupByProp(
                                  items,
                                  'sectionHandle',
                                )"
                                :key="sectionHandle"
                              >
                                <ul class="space-y-2">
                                  <li v-for="item in groupItems" :key="item.objectID">
                                    <search-item-card
                                      :title="item.title"
                                      :url="item.url"
                                      :breadcrumbs="item.breadcrumbs"
                                    />
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </template>
                        </ais-hits>
                        <p v-else class="text-center text-sm font-medium">
                          No results have been found for
                          <span class="font-semibold">{{ query }}</span
                          >.
                        </p>
                      </template>
                    </ais-state-results>
                  </div>
                  <p v-else class="text-center text-sm font-medium">Start typing to search...</p>
                </template>
              </ais-stats>
            </div>
          </ais-instant-search>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
